import { parseRoleName } from "../utils/parseRoleName";
import type { RoleRow } from "./roleViewOverridePresets";

export { parseRoleName };

/**
 * Session rows (RoleOnly) → flat role names as in company user admin /users/roles tree.
 */
export function sessionRoleRowsToActiveFlatNames(rows: RoleRow[]): string[] {
  const out = new Set<string>();
  for (const r of rows) {
    const can = r.can || [];
    if (can.includes("*")) {
      out.add(r.name);
      continue;
    }
    if (can.length === 0) {
      out.add(r.name);
      continue;
    }
    const p = parseRoleName(r.name);
    if (p.type === "main" || p.type === "submodule") {
      out.add(r.name);
      for (const a of can) {
        out.add(`${r.name}_${a}`);
      }
    } else {
      out.add(r.name);
      for (const a of can) {
        out.add(`${r.name}_${a}`);
      }
    }
  }
  return Array.from(out);
}

type SubState = { main: boolean; actions: Set<string> };

/**
 * Flat active names from role tree → session RoleRow[] for RoleOnly / hooks.
 */
export function activeFlatNamesToSessionRoleRows(
  activeNames: string[]
): RoleRow[] {
  const active = new Set(activeNames);
  const moduleActions = new Map<string, Set<string>>();
  const moduleMainOn = new Set<string>();
  const submoduleState = new Map<string, SubState>();

  Array.from(active).forEach((fullName) => {
    const p = parseRoleName(fullName);
    if (p.type === "main") {
      moduleMainOn.add(fullName);
      if (!moduleActions.has(fullName)) moduleActions.set(fullName, new Set());
    } else if (p.type === "action") {
      const mod = p.module as string;
      if (!moduleActions.has(mod)) moduleActions.set(mod, new Set());
      moduleActions.get(mod)!.add(p.action as string);
    } else if (p.type === "submodule") {
      if (!submoduleState.has(fullName)) {
        submoduleState.set(fullName, { main: false, actions: new Set() });
      }
      submoduleState.get(fullName)!.main = true;
    } else if (p.type === "submodule_action") {
      const fullKey = `${p.module}_${p.submodule}`;
      if (!submoduleState.has(fullKey)) {
        submoduleState.set(fullKey, { main: false, actions: new Set() });
      }
      submoduleState.get(fullKey)!.actions.add(p.action as string);
    } else {
      moduleMainOn.add(fullName);
      if (!moduleActions.has(fullName)) moduleActions.set(fullName, new Set());
    }
  });

  const rows: RoleRow[] = [];
  const allModules = new Set([
    ...Array.from(moduleMainOn),
    ...Array.from(moduleActions.keys()),
  ]);
  Array.from(allModules).forEach((mod) => {
    const acts = moduleActions.get(mod) || new Set<string>();
    const main = moduleMainOn.has(mod);
    let can = Array.from(acts);
    if (main && can.length === 0) {
      can = ["*"];
    } else if (!main && can.length === 0) {
      return;
    }
    rows.push({ name: mod, can });
  });

  Array.from(submoduleState.entries()).forEach(([fullKey, { main, actions }]) => {
    let can = Array.from(actions);
    if (main && can.length === 0) {
      can = ["*"];
    } else if (!main && can.length === 0) {
      return;
    }
    rows.push({ name: fullKey, can });
  });

  return rows;
}
